import * as MatchStatus from 'customer-data-filters-ui/filterQueryFormat/MatchStatus';
import I18n from 'I18n';
import { useViewerActions } from '../../hooks/useViewerActions';
import ViewerConditionClickableBox from '../viewer/ViewerConditionClickableBox';
import ViewerCondition from './ViewerCondition';
import { useCallback, useContext } from 'react';
import ViewerRelatedConditionsHeading from './ViewerRelatedConditionsHeading';
import ViewerAddFilterButton from './ViewerAddFilterButton';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import ViewerConditionPlaceholder from './ViewerConditionPlaceholder';
import { isLogicGroup, LogicGroupNames } from '../../filterQueryFormat/logic/LogicGroup';
import RefHighlightContainer from '../common/RefHighlightContainer';
import getConditionId from '../../utils/getConditionId';
import { isAssociationBranch } from '../../strategy/InboundDbListSegStrategyUtils';
import { isPropertyNameAndValueCondition } from '../../strategy/propertyChangedFilterUtils';
import { useStrategy } from '../../hooks/useStrategy';
import { Text } from 'foundations-components/components/Text';
import ConditionLogicBox from '../common/ConditionLogicBox';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import styled from 'foundations-theming/utils';
import { isAnyEventTypeId } from '../../filterQueryFormat/ObjectTypeId';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LOGIC_OPERATOR_GAP = '100';
const StyledConditionContentBox = styled.div.withConfig({
  displayName: "ViewerRelatedConditions__StyledConditionContentBox",
  componentId: "sc-1eq7rll-0"
})(({
  withoutLogicBox,
  theme
}) => ({
  padding: theme.space[300],
  backgroundColor: theme.color['fill-surface-default'],
  borderRadius: withoutLogicBox ? theme.borderRadius[100] : `0 0 ${theme.borderRadius[100]} ${theme.borderRadius[100]}`
}));
const StyledConditionBox = styled.div.attrs({
  'data-test-id': 'condition-box',
  'data-highlight-target': 'condition-box'
})(({
  useOnePanel,
  deleteHighlight,
  matchPass,
  matchFail,
  withError,
  inEditMode,
  theme
}) => {
  const getBorderStyle = () => {
    if (deleteHighlight) {
      return {
        borderColor: theme.color['border-alert-default'],
        outline: useOnePanel ? undefined : `1px solid ${theme.color['border-alert-default']}`
      };
    } else if (matchPass) {
      return {
        borderLeft: `4px solid ${theme.color['fill-positive-default']}`
      };
    } else if (matchFail) {
      return {
        borderLeft: `4px solid ${theme.color['border-alert-default']}`
      };
    } else if (withError) {
      return {
        borderColor: `${theme.color['border-alert-default']} !important`,
        outline: useOnePanel ? undefined : `1px solid ${theme.color['border-alert-default']}`
      };
    } else if (inEditMode) {
      return {
        borderColor: theme.color['border-core-default']
      };
    }
  };
  return Object.assign({
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.color['border-core-hover']}`,
    borderRadius: theme.borderRadius[100]
  }, getBorderStyle());
});
const ConditionsList = styled.div.withConfig({
  displayName: "ViewerRelatedConditions__ConditionsList",
  componentId: "sc-1eq7rll-1"
})(({
  theme
}) => ({
  '> *': {
    position: 'relative',
    marginTop: theme.space[50],
    marginLeft: theme.space[100]
  }
}));
const StyledAddFilterButtonContainer = styled.div.withConfig({
  displayName: "ViewerRelatedConditions__StyledAddFilterButtonContainer",
  componentId: "sc-1eq7rll-2"
})(({
  isAssociation
}) => Object.assign({
  marginTop: '6px'
}, isAssociation ? {} : {
  marginLeft: `4px`,
  '&::after': {
    top: '-6px'
  }
}));
const ViewerRelatedConditions = props => {
  const {
    isFirst,
    logicGroup,
    matchStatusData,
    parentLogicGroupOperator,
    conditionPath
  } = props;
  const {
    dispatch,
    isInEditMode,
    draftCondition,
    viewerConditionIdToHighlight,
    viewerConditionToDelete,
    viewerConditionToDeleteHovered,
    props: {
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    isTestFiltersEnabled
  } = useStrategy();
  const {
    onEditFilter
  } = useViewerActions();
  const logicGroupConditionsSeq = logicGroup.conditions.entrySeq();
  const isAddingNewCondition = draftCondition && draftCondition.isAddingNew && draftCondition.conditionPath.equals(conditionPath);
  const isAssociation = isAssociationBranch(logicGroup);
  const hasPropertyNameAndValueCondition = logicGroup.conditions.valueSeq().some(isPropertyNameAndValueCondition);
  const onConditionHighlightFinish = useCallback(() => {
    dispatch({
      type: 'viewerConditionIdToHighlight',
      payload: undefined
    });
  }, [dispatch]);
  const onSubFilterClick = useCallback((evt, path, filterFamily) => {
    evt.stopPropagation();
    onEditFilter(path, filterFamily);
  }, [onEditFilter]);
  const matchStatus = matchStatusData && matchStatusData.status;
  const testFiltersData = matchStatusData && matchStatusData.filtersData;
  const logicOperator = isFirst ? undefined : parentLogicGroupOperator;
  const hasLogicBox = isInEditMode || !isFirst;
  const internalLogicOperator = logicGroup.logicGroupName;
  const getLogicSeparator = (visuallyHidden = false) => {
    return /*#__PURE__*/_jsx(Box, {
      flexGrow: 0,
      paddingBlockStart: "100",
      children: /*#__PURE__*/_jsx("div", {
        style: {
          visibility: visuallyHidden ? 'hidden' : 'visible'
        },
        children: internalLogicOperator === LogicGroupNames.AND ? I18n.text('newCustomerDataFilters.logicOperator.and') : I18n.text('newCustomerDataFilters.logicOperator.or')
      })
    });
  };
  return /*#__PURE__*/_jsxs(StyledConditionBox, {
    useOnePanel: !!useOnePanel,
    deleteHighlight: !!(viewerConditionToDelete && conditionPath.equals(viewerConditionToDelete) || viewerConditionToDeleteHovered && conditionPath.equals(viewerConditionToDeleteHovered)),
    matchPass: !!(useOnePanel && isTestFiltersEnabled && matchStatus === MatchStatus.PASS),
    matchFail: !!(useOnePanel && isTestFiltersEnabled && matchStatus === MatchStatus.FAIL),
    withError: !!logicGroup.errors.length,
    inEditMode: !!(useOnePanel && isInEditMode),
    children: [hasLogicBox && /*#__PURE__*/_jsx(ConditionLogicBox, {
      conditionPath: conditionPath,
      logicOperator: logicOperator
    }), /*#__PURE__*/_jsxs(StyledConditionContentBox, {
      withoutLogicBox: !hasLogicBox,
      children: [/*#__PURE__*/_jsx(RefHighlightContainer, {
        onHighlightFinish: onConditionHighlightFinish,
        shouldHighlight: !!useOnePanel && getConditionId(conditionPath) === viewerConditionIdToHighlight,
        children: /*#__PURE__*/_jsx(ViewerRelatedConditionsHeading, {
          conditionPath: conditionPath,
          logicGroup: logicGroup,
          matchStatus: matchStatus
        })
      }), /*#__PURE__*/_jsxs(ConditionsList, {
        children: [logicGroupConditionsSeq.map(([key, condition], index) => {
          const currConditionPath = conditionPath.push(key);
          const testFilterData = testFiltersData && testFiltersData[index];
          if (isLogicGroup(condition)) {
            const conditionIds = condition.conditions.entrySeq().map(([conditionKey]) => getConditionId(currConditionPath.push(conditionKey))).concat(getConditionId(currConditionPath));
            const shouldHighlight = !useOnePanel && conditionIds.includes(viewerConditionIdToHighlight);
            return /*#__PURE__*/_jsx(RefHighlightContainer, {
              onHighlightFinish: onConditionHighlightFinish,
              shouldHighlight: shouldHighlight,
              targetSelector: ".condition-box",
              children: /*#__PURE__*/_jsx(ViewerRelatedConditions, {
                conditionPath: currConditionPath,
                logicGroup: condition,
                parentLogicGroupOperator: LogicGroupNames.AND
              })
            }, `${index}-${key}`);
          }
          return /*#__PURE__*/_jsxs(Flex, {
            gap: isInEditMode ? LOGIC_OPERATOR_GAP : '0',
            children: [getLogicSeparator(index === 0 && !isAnyEventTypeId(logicGroup.filterFamily)), /*#__PURE__*/_jsx(Box, {
              flex: 1,
              minInlineSize: "0",
              children: /*#__PURE__*/_jsx(RefHighlightContainer, {
                onHighlightFinish: onConditionHighlightFinish,
                shouldHighlight: !!useOnePanel && getConditionId(currConditionPath) === viewerConditionIdToHighlight,
                children: /*#__PURE__*/_jsx(ViewerConditionClickableBox, {
                  conditionPath: currConditionPath,
                  condition: condition,
                  filterFamily: logicGroup.filterFamily,
                  hasDeleteButton: true,
                  onClick: evt => onSubFilterClick(evt, currConditionPath, logicGroup.filterFamily),
                  testFilterData: testFilterData,
                  use: "hover",
                  isInConditionContentBox: true,
                  children: /*#__PURE__*/_jsx(ViewerCondition, {
                    condition: condition,
                    filterFamily: logicGroup.filterFamily,
                    parentLogicGroup: logicGroup,
                    testFilterData: testFilterData,
                    use: "plain"
                  })
                })
              })
            })]
          }, `${index}-${key}`);
        }), isAddingNewCondition && /*#__PURE__*/_jsxs(Flex, {
          gap: LOGIC_OPERATOR_GAP,
          children: [getLogicSeparator(), /*#__PURE__*/_jsx(Box, {
            flex: 1,
            children: /*#__PURE__*/_jsx(ViewerConditionPlaceholder, {})
          })]
        }), isInEditMode && !hasPropertyNameAndValueCondition && /*#__PURE__*/_jsx(StyledAddFilterButtonContainer, {
          isAssociation: isAssociation,
          children: /*#__PURE__*/_jsxs(Flex, {
            gap: LOGIC_OPERATOR_GAP,
            children: [/*#__PURE__*/_jsx(Box, {
              paddingBlockEnd: "100",
              children: getLogicSeparator()
            }), /*#__PURE__*/_jsx(ViewerAddFilterButton, {
              parentGroupKeyPath: conditionPath,
              filterFamily: logicGroup.filterFamily
            })]
          })
        })]
      }), logicGroup.errors.map((error, index) => /*#__PURE__*/_jsx(Text, {
        display: "block",
        marginBlockStart: "200",
        typography: "heading-25",
        use: "error",
        children: error.message
      }, `${conditionPath.join('_')}_error_${index}`))]
    })]
  });
};
export default ViewerRelatedConditions;